import React, { useEffect, useMemo, useState } from 'react';
import { X, Link2, Save, AlertTriangle, CheckCircle, HelpCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { MATCH_STATUS, buildAllocationLinkRows, buildEmployeeMatchGroups } from '../core/employeeMatching';
import { writeRows } from '../data/appsheet';

const STATUS_INFO = {
    [MATCH_STATUS.DUPLICATE]: { label: 'Trùng tên', className: 'bg-red-100 text-red-700', icon: AlertTriangle },
    [MATCH_STATUS.UNMATCHED]: { label: 'Không khớp', className: 'bg-orange-100 text-orange-700', icon: HelpCircle },
    [MATCH_STATUS.MATCHED]: { label: 'Khớp 1 người', className: 'bg-green-100 text-green-700', icon: CheckCircle },
    [MATCH_STATUS.LINKED]: { label: 'Đã gắn', className: 'bg-gray-100 text-gray-600', icon: Link2 },
};

const FILTERS = [
    { value: 'CAN_XU_LY', label: 'Cần xử lý' },
    { value: MATCH_STATUS.MATCHED, label: 'Khớp 1 người' },
    { value: MATCH_STATUS.LINKED, label: 'Đã gắn' },
    { value: 'ALL', label: 'Tất cả' },
];

/**
 * Gắn SỐ HĐ PHỤ TRÁCH cho các dòng TO_PBNS cũ chỉ ghi tên. Nhóm khớp đúng một người được chọn
 * sẵn; trùng tên hoặc không khớp phải chọn tay. Để trống nghĩa là không phải người (máy, đội)
 * và dòng đó sẽ không tính vào lương ai.
 */
const GhepSoHdModal = ({ allocations, employees, onClose, onSaved }) => {
    const groups = useMemo(() => buildEmployeeMatchGroups(allocations, employees), [allocations, employees]);
    const [selected, setSelected] = useState({});
    const [filter, setFilter] = useState('CAN_XU_LY');
    const [progress, setProgress] = useState(null);

    useEffect(() => {
        setSelected(Object.fromEntries(groups.map((group) => [group.key, group.suggestedSoHd])));
    }, [groups]);

    const sortedEmployees = useMemo(
        () => [...employees]
            .filter((employee) => employee['SỐ HĐ'])
            .sort((left, right) => String(left['HỌ VÀ TÊN'] || '').localeCompare(String(right['HỌ VÀ TÊN'] || ''), 'vi')),
        [employees],
    );

    const counts = useMemo(() => groups.reduce((result, group) => {
        result[group.status] = (result[group.status] || 0) + 1;
        return result;
    }, {}), [groups]);

    const visibleGroups = groups.filter((group) => {
        if (filter === 'ALL') return true;
        if (filter === 'CAN_XU_LY') return group.status === MATCH_STATUS.DUPLICATE || group.status === MATCH_STATUS.UNMATCHED;
        return group.status === filter;
    });

    const rowsToWrite = buildAllocationLinkRows(groups, selected);
    const isSaving = progress !== null;

    const handleSave = async () => {
        if (!rowsToWrite.length) {
            toast.info('Không có dòng nào cần gắn Số HĐ');
            return;
        }
        if (!window.confirm(`Gắn Số HĐ cho ${rowsToWrite.length} dòng phân bổ?`)) return;
        try {
            setProgress({ done: 0, total: rowsToWrite.length });
            await writeRows('TO_PBNS', 'Edit', rowsToWrite, (done, total) => setProgress({ done, total }));
            toast.success(`Đã gắn Số HĐ cho ${rowsToWrite.length} dòng phân bổ`);
            await onSaved?.();
            onClose();
        } catch (error) {
            console.error('Error linking SO HD:', error);
            toast.error('Có lỗi khi ghi Số HĐ: ' + (error.message || '') + '. Có thể bấm lưu lại, các dòng đã ghi sẽ không bị trùng.');
        } finally {
            setProgress(null);
        }
    };

    const renderOptions = (group) => {
        const candidateSoHds = new Set(group.candidates.map((candidate) => String(candidate['SỐ HĐ'])));
        return (
            <>
                <option value="">— Để trống (máy / đội, không tính lương) —</option>
                {group.candidates.length > 0 && (
                    <optgroup label="Cùng tên">
                        {group.candidates.map((candidate) => (
                            <option key={candidate['SỐ HĐ']} value={candidate['SỐ HĐ']}>
                                {candidate['SỐ HĐ']} — {candidate['HỌ VÀ TÊN']}{candidate['TỔ'] ? ` (${candidate['TỔ']})` : ''}
                            </option>
                        ))}
                    </optgroup>
                )}
                <optgroup label="Tất cả nhân sự">
                    {sortedEmployees
                        .filter((employee) => !candidateSoHds.has(String(employee['SỐ HĐ'])))
                        .map((employee) => (
                            <option key={employee['SỐ HĐ']} value={employee['SỐ HĐ']}>
                                {employee['SỐ HĐ']} — {employee['HỌ VÀ TÊN']}{employee['TỔ'] ? ` (${employee['TỔ']})` : ''}
                            </option>
                        ))}
                </optgroup>
            </>
        );
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 md:p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col">
                <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-indigo-100">
                    <div>
                        <h2 className="text-lg font-bold text-indigo-900 flex items-center gap-2">
                            <Link2 className="w-5 h-5" />
                            Ghép Số HĐ cho nhân sự phụ trách
                        </h2>
                        <p className="text-xs text-indigo-700 mt-0.5">
                            Bảng lương tính năng suất theo Số HĐ. Dòng để trống Số HĐ sẽ không tính vào lương ai.
                        </p>
                    </div>
                    <button onClick={onClose} disabled={isSaving} className="p-1.5 hover:bg-indigo-200 rounded-lg disabled:opacity-50">
                        <X className="w-5 h-5 text-gray-600" />
                    </button>
                </div>

                <div className="p-4 border-b border-gray-200 flex flex-wrap items-center gap-2">
                    {[MATCH_STATUS.DUPLICATE, MATCH_STATUS.UNMATCHED, MATCH_STATUS.MATCHED, MATCH_STATUS.LINKED].map((status) => {
                        const { label, className, icon: Icon } = STATUS_INFO[status];
                        return (
                            <span key={status} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${className}`}>
                                <Icon className="w-3.5 h-3.5" />
                                {label}: {counts[status] || 0}
                            </span>
                        );
                    })}
                    <div className="ml-auto flex gap-1">
                        {FILTERS.map((item) => (
                            <button
                                key={item.value}
                                onClick={() => setFilter(item.value)}
                                className={`px-2.5 py-1 text-xs rounded-lg border ${filter === item.value
                                    ? 'bg-indigo-600 text-white border-indigo-600'
                                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'}`}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {visibleGroups.length === 0 ? (
                        <div className="text-center py-12 text-sm text-gray-500">Không có dòng nào trong mục này</div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                                <tr>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Nhân sự phụ trách</th>
                                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700">Số dòng</th>
                                    <th className="px-3 py-2 text-center text-xs font-semibold text-gray-700">Kết quả ghép</th>
                                    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700">Số HĐ</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {visibleGroups.map((group) => {
                                    const { label, className } = STATUS_INFO[group.status];
                                    const isLinked = group.status === MATCH_STATUS.LINKED;
                                    return (
                                        <tr key={group.key} className="hover:bg-gray-50">
                                            <td className="px-3 py-2 text-xs font-medium text-gray-900">{group.name}</td>
                                            <td className="px-3 py-2 text-xs text-center text-gray-700" title={group.allocationIds.join('\n')}>
                                                {group.allocationIds.length}
                                            </td>
                                            <td className="px-3 py-2 text-center">
                                                <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${className}`}>{label}</span>
                                            </td>
                                            <td className="px-3 py-2">
                                                {isLinked ? (
                                                    <span className="text-xs text-gray-600">{group.suggestedSoHd}</span>
                                                ) : (
                                                    <select
                                                        value={selected[group.key] ?? ''}
                                                        onChange={(e) => setSelected((prev) => ({ ...prev, [group.key]: e.target.value }))}
                                                        disabled={isSaving}
                                                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                                    >
                                                        {renderOptions(group)}
                                                    </select>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                <div className="flex items-center justify-between gap-2 p-4 border-t border-gray-200 bg-gray-50">
                    <span className="text-xs text-gray-600">
                        {isSaving
                            ? `Đang ghi ${progress.done}/${progress.total} dòng...`
                            : `Sẽ gắn Số HĐ cho ${rowsToWrite.length} dòng phân bổ`}
                    </span>
                    <div className="flex gap-2">
                        <button
                            onClick={onClose}
                            disabled={isSaving}
                            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-50"
                        >
                            Đóng
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={isSaving || rowsToWrite.length === 0}
                            className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Save className="w-4 h-4" />
                            Lưu
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GhepSoHdModal;
